import type { ArchiveResult } from './trash'
import { api, type Page } from './client'

export interface Target {
  id: number
  name: string
  description: string | null
  targetType: string
  scopeType: string
  scopeId: number | null
  targetValue: number
  startDate: string
  endDate: string
  status: string
  currentValue: number
  percentage: number
  createdAt: string
}

export interface Contest {
  id: number
  name: string
  description: string | null
  contestType: string
  /** Optional operator NAME (not id) a contest is scoped to — matched case-insensitively
   * with trim server-side by `ContractContributionReader` (TASK-0038). `null`/absent means
   * the contest is unscoped by operator (counts contributions from every operator). Only
   * present on the `GET /api/contests/{id}` detail response (`ContestDetail`), not on list
   * rows — `ContestsController.List`'s projection omits it (TASK-0040 investigation). */
  targetOperator?: string | null
  /** Optional free-text product type, matched against `Contract.ContractType` the same way
   * (case-insensitive, trimmed). Free text because `Contract.ContractType` itself is a free
   * text field on the backend, not an enum — see `ContractsPage.tsx`'s own "Tipo Prodotto"
   * filter for the convention. Same list-vs-detail availability caveat as `targetOperator`. */
  targetProductType?: string | null
  /** TASK-0161 — the declared numeric goal set by an admin at creation/edit time. Unlike
   * `targetOperator`/`targetProductType` above, this IS present on both the list projection
   * (`ContestsController.List`) and the detail response — it is configuration data (same
   * category as `prize`/`contestType`/the dates), not the live per-agent contract revenue that
   * `participants[].currentValue`/`rank` remain redacted for (TASK-0089). No progress
   * percentage is derived from it here — see `ContestDetailPage.tsx`. Typed `| null`, not just
   * optional: the backend has no global `DefaultIgnoreCondition` (`Program.cs`'s `AddControllers()`
   * uses no JSON options), so a `decimal?` with no value serializes as `"targetValue": null` on the
   * wire, not an absent key — true of every contest that predates this task. */
  targetValue?: number | null
  prize: string | null
  startDate: string
  endDate: string
  status: string
  participantCount: number
  createdAt: string
}

export interface ContestDetail extends Contest {
  targetOperator: string | null
  targetProductType: string | null
  participants: Array<{
    id: number
    userId: number
    currentValue: number
    rank: number
    joinedAt: string
  }>
}

export const targetsApi = {
  list: (p?: { page?: number; status?: string; scopeType?: string }) => {
    const params = new URLSearchParams()
    if (p?.page) params.set('page', String(p.page))
    if (p?.status) params.set('status', p.status)
    if (p?.scopeType) params.set('scopeType', p.scopeType)
    return api<Page<Target>>(`/api/targets?${params}`)
  },
  get: (id: number) => api<Target>(`/api/targets/${id}`),
  create: (data: Partial<Target> & { name: string; targetType: string; scopeType: string; targetValue: number; startDate: string; endDate: string }) =>
    api<{ id: number }>('/api/targets', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: Partial<Target>) =>
    api<void>(`/api/targets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateStatus: (id: number, status: string) =>
    api<void>(`/api/targets/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  /** F-104: `200 { archived: true }` se l'obiettivo ha un percorso (annullato), `204` se era ancora a zero (rimosso). */
  delete: (id: number) => api<ArchiveResult | undefined>(`/api/targets/${id}`, { method: 'DELETE' }),
}

export const contestsApi = {
  list: (p?: { page?: number; status?: string }) => {
    const params = new URLSearchParams()
    if (p?.page) params.set('page', String(p.page))
    if (p?.status) params.set('status', p.status)
    return api<Page<Contest>>(`/api/contests?${params}`)
  },
  get: (id: number) => api<ContestDetail>(`/api/contests/${id}`),
  create: (data: Partial<Contest> & { name: string; contestType: string; startDate: string; endDate: string }) =>
    api<{ id: number }>('/api/contests', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: Partial<Contest>) =>
    api<void>(`/api/contests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateStatus: (id: number, status: string) =>
    api<void>(`/api/contests/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  /** F-104: `200 { archived: true }` se la gara ha partecipanti o non e' in bozza (annullata), `204` se era una bozza vuota. */
  delete: (id: number) => api<ArchiveResult | undefined>(`/api/contests/${id}`, { method: 'DELETE' }),
}
